import { ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";

import { Datagrid } from "@/components/_organisms/datagrid/datagrid";
import { DataGridData } from "@/components/_organisms/datagrid/use-data-grid-config";
import { parsePastedValue } from "@/components/_organisms/datagrid/utils/parse-pasted-value";
import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";
import { GhgInputCell, GhgInputRightAside } from "@/domain/inventory/components/sector-tabs/ghg/ghg-input-right-aside";
import { GRID_CELL_COLORS } from "@/domain/inventory/const";
import { MunicipalPropertyConsumptionAutosave } from "@/domain/municipal-property/hooks/use-municipal-property-consumption-autosave";
import { getConsumptionCellKey } from "@/domain/municipal-property/models/get-consumption-cell-key";
import { MunicipalPropertyYearQuery } from "@/graphql/generated/graphql";
import { fromJoules, GHG_ADMINISTRATION_METHODOLOGY_LOOKUP_MAP, MUNICIPAL_PROPERTY_VALUE_CELL_POLICY, toGhgQuality } from "@ark/shared";
import { Item } from "@glideapps/glide-data-grid";

type ValueCell = (typeof MUNICIPAL_PROPERTY_VALUE_CELL_POLICY.cellList)[number];
interface Props {
  year: MunicipalPropertyYearQuery["municipalPropertyYear"];
  canWrite: boolean;
  autosave: MunicipalPropertyConsumptionAutosave;
  cellList: readonly ValueCell[];
}

export function MunicipalPropertyConsumptionGrid({ year, canWrite, autosave, cellList }: Props): ReactElement {
  const { t, i18n } = useDomainTranslation("municipal-property");
  const { t: translateInventory } = useTranslation("inventory");
  const { draftMap, handleCellUpdate } = autosave;
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const data: DataGridData[] = [];
  const readOnlyRows: string[] = [];
  let previousScope: number | undefined;
  for (const cell of cellList) {
    if (cell.scope !== previousScope) {
      const title = translateInventory("calculatedSector.scopeLabel", { scope: cell.scope });
      readOnlyRows.push(title.toUpperCase());
      data.push(Object.fromEntries(["firstColumn", "AMOUNT", "SOURCE"].map((columnId) => [columnId, {
        value: columnId === "firstColumn" ? title : "",
        displayData: columnId === "firstColumn" ? title : "",
        themeOverride: { bgCell: GRID_CELL_COLORS.scopeHeaderBg },
      }])));
      previousScope = cell.scope;
    }
    const key = getConsumptionCellKey(cell);
    const stored = year.valueList.find((value) => getConsumptionCellKey(value) === key);
    const draft = draftMap.get(`${year.id}:${key}`);
    const amount = draft ? draft.amount : stored ? String(cell.storageUnit === "J" ? fromJoules(stored.amount, "MWh") : stored.amount) : "";
    const label = `${t(`register.property.energySource.${cell.energySourceKey}`)} (${cell.inputUnit})`;
    data.push({
      firstColumn: { value: key, displayData: label, themeOverride: { bgCell: GRID_CELL_COLORS.labelBg } },
      AMOUNT: { value: amount === "" ? "" : Number(amount), ...(amount === "" ? { displayData: "" } : {}) },
      SOURCE: { value: draft?.dataSource ?? stored?.dataSource ?? "" },
    });
  }
  const selectedPolicy = cellList.find((cell) => getConsumptionCellKey(cell) === selectedKey);
  const selectedRow = data.find((row) => row.firstColumn.value === selectedKey);
  const selectedSector = selectedPolicy ? GHG_ADMINISTRATION_METHODOLOGY_LOOKUP_MAP.sectors[selectedPolicy.sectorKey] : null;
  const selectedValue = year.valueList.find((value) => getConsumptionCellKey(value) === selectedKey);
  const selectedDraft = selectedKey ? draftMap.get(`${year.id}:${selectedKey}`) : undefined;
  const selectedCell: GhgInputCell | null = selectedPolicy && selectedRow && selectedSector ? {
    energySourceId: selectedPolicy.sectorKey === "MUNICIPAL_FACILITIES"
      ? GHG_ADMINISTRATION_METHODOLOGY_LOOKUP_MAP.sectors.MUNICIPAL_FACILITIES.energySources[selectedPolicy.energySourceKey].id
      : GHG_ADMINISTRATION_METHODOLOGY_LOOKUP_MAP.sectors.WATER_CONSUMPTION.energySources.WATER.id,
    energySourceTitle: t(`register.property.energySource.${selectedPolicy.energySourceKey}`),
    sectorId: selectedSector.id,
    subsectorId: year.propertyId,
    subsectorTitle: year.name,
    subsubsectorId: null,
    scope: selectedPolicy.scope,
    amount: Number(selectedRow.AMOUNT.value),
    displayValue: selectedRow.AMOUNT.value === "" ? "–" : Number(selectedRow.AMOUNT.value).toLocaleString(i18n?.language),
    unit: selectedPolicy.inputUnit,
    gpcQualityRating: toGhgQuality(selectedDraft?.gpcQualityRating ?? selectedValue?.gpcQualityRating),
    referenceYear: year.year,
    dataSource: String(selectedRow.SOURCE.value) || null,
    inventoryValueId: null,
    isReadOnly: true,
  } : null;
  function handlePaste([columnIndex, rowIndex]: Item, valueList: readonly (readonly string[])[]): boolean {
    if (!canWrite) return false;
    valueList.forEach((rowValueList, rowOffset) => {
      const row = data[rowIndex + rowOffset];
      if (!row || !cellList.some((cell) => getConsumptionCellKey(cell) === row.firstColumn.value)) return;
      rowValueList.forEach((rawValue, columnOffset) => {
        if (columnIndex + columnOffset !== 1) return;
        const value = rawValue.trim() === "" ? null : parsePastedValue(rawValue);
        if (rawValue.trim() !== "" && (value === null || !Number.isFinite(value) || value < 0)) return;
        handleCellUpdate({ rowId: String(row.firstColumn.value), columnId: "AMOUNT", value });
      });
    });
    return false;
  }
  return <>
    <Datagrid
      data={data}
      columns={[t("register.property.columns.energySource"), t("register.property.columns.consumption")]}
      columnIds={["ENERGY_SOURCE", "AMOUNT"]}
      readOnlyColumns={canWrite ? ["ENERGY_SOURCE"] : ["ENERGY_SOURCE", "AMOUNT"]}
      readOnlyRows={readOnlyRows}
      handleCellSelection={({ rowId }) => setSelectedKey(cellList.some((cell) => getConsumptionCellKey(cell) === rowId) ? rowId : null)}
      handleCellUpdate={canWrite ? handleCellUpdate : undefined}
      onPaste={handlePaste}
    />
    <GhgInputRightAside selectedCell={selectedCell} isEditing={canWrite}
      onQualityChange={selectedKey ? (value) => handleCellUpdate({ rowId: selectedKey, columnId: "QUALITY", value }) : undefined}
      onDataSourceChange={selectedKey ? (value) => handleCellUpdate({ rowId: selectedKey, columnId: "SOURCE", value }) : undefined} />
  </>;
}
